import { BlockWrapper, defineBlock } from "@smartfire/js-block-engine";
import apiFetch from "@wordpress/api-fetch";
import { useCallback, useEffect, useRef, useState } from "react";
import { Pagination, Product, LoadingProduct } from "../../components";
import { useSelectedFilters } from "../../hooks";
import { ignoreOrderCompare } from "../../shared/tools";
import { fireTotalProductEvent } from "../customEvents";
import style from "./block.module.scss";

const Block = ({
  initialProducts,
  initialPage,
  initialNbProducts,
  initialFilters,
  initialUniverse = null,
  perPage,
  buyWithAmazon = false,
}) => {
  const { selectedFilters } = useSelectedFilters(initialFilters);
  const [products, setProducts] = useState(initialProducts);
  const [page, setPage] = useState(initialPage);
  const [totalProducts, setTotalProducts] = useState(initialNbProducts);
  const isMounted = useRef(false);
  const numberOfPages = Math.ceil(totalProducts / perPage);
  const [isLoading, setLoading] = useState(false);
  const skeletons = [...Array(perPage).keys()];

  /**
   * Formats result data of api request
   */
  const formatRestData = (data) => {
    return data.map((el) => {
      return {
        id: el.ID,
        brand: el.sbm_data.brand,
        link: el.link,
        title: el.title.rendered ?? "",
        image: el.sbm_data.image,
        enableBuyOnline: el.sbm_data.enableBuyOnline ?? false,
        buyOnlineLink: el.sbm_data.buyOnlineLink ?? "",
      };
    });
  };

  /**
   * Set query params with filters and page
   */
  const setQueryParams = useCallback((selectedFilters, page, setPage) => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set("page", page.toString());

    let nbFilters = 0;
    Object.values(selectedFilters).forEach((filterType) => {
      nbFilters += Object.keys(filterType).length;
    });

    let filters = "";
    Object.values(selectedFilters).forEach((filterType) => {
      const filterIds = Object.keys(filterType);
      filters += filterIds.join(",");
      if (filterIds.length > 0) {
        filters += ",";
      }
    });
    filters = filters.substring(0, filters.length - 1);

    if (nbFilters === 0) {
      urlParams.delete("filters");
    } else {
      // Detect if we changed filters, and so go to the first page
      const currentFilters = urlParams.get("filters");
      const a = currentFilters ? currentFilters.split(",") : [];
      const b = filters ? filters.split(",") : [];
      if (!ignoreOrderCompare(a, b)) {
        setPage(1);
      }
      // Update filters
      urlParams.set("filters", filters);
    }

    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${urlParams}`
    );
  }, []);

  /**
   * Get products when selected filters change
   */
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    setLoading(true);

    // Set URL parameters
    setQueryParams(selectedFilters, page, setPage);

    // Init API request parameters
    let params = {
      page: page,
      per_page: perPage,
      ...(initialUniverse && { product_universe: initialUniverse }),
    };

    // Add filters arguments to params
    Object.keys(selectedFilters).forEach((filterType) => {
      const filters = selectedFilters[filterType];

      if (Object.values(filters).length > 0) {
        params[filterType] = Object.keys(filters);
      }
    });
    params = new URLSearchParams(params).toString();

    // Get product by page and filters
    apiFetch({
      path: "/wp/v2/product?" + params,
      parse: false,
    })
      .then((res) => {
        res.json().then((data) => setProducts(formatRestData(data)));
        const total = res.headers.get("X-WP-Total");
        setTotalProducts(total);
        fireTotalProductEvent(total);
      })
      .finally(() => setLoading(false));
  }, [selectedFilters, page, perPage, initialUniverse, setQueryParams]);

  /**
   * Scroll to the top of the listing after page changed
   */
  useEffect(() => {
    if (isMounted) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [page]);

  return (
    <BlockWrapper.Div className={style.block}>
      {products.length > 0 && (
        <div className={style.listing}>
          {!isLoading
            ? products.map((product, i) => (
                <Product
                  key={`${product.id}-${i}`}
                  product={product}
                  className={style.product}
                  buyWithAmazon={buyWithAmazon}
                />
              ))
            : skeletons.map((skeleton) => (
                <div key={skeleton} className={style.postSkeleton}>
                  <LoadingProduct />
                </div>
              ))}
        </div>
      )}
      {numberOfPages > 1 && (
        <Pagination
          currentPagination={page}
          numberOfPages={numberOfPages}
          setCurrentPage={setPage}
        />
      )}
    </BlockWrapper.Div>
  );
};

export default defineBlock({
  Block,
});
